import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LoadingController,
  NavController,
  Platform,
  PopoverController,
} from '@ionic/angular';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { FunctionSwitchHelperService } from 'src/app/providers/component-helpers/function-switch.service';
import { ToastService } from 'src/packages/mitsBasics';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { ToggleMenuHelper } from 'src/app/providers/component-helpers/toggle-menu-helper.service';
import { ErrorService } from 'src/packages/mitsBasics';
import { IDataService } from 'src/packages/mitsBasics';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { DetailPageService } from 'src/app/providers/page-services/detailpage.service';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { AccessGuard } from 'src/app/providers/access.guard';
import { LanguageService, MitsLanguage } from 'src/packages/mitsBasics';
import { BasicModel } from 'src/packages/mitsBasics';
import {
  IRegExAssociation,
  ITypeAssociation,
  REGEX_ASSOCIATIONS,
  TYPE_ASSOCIATIONS,
} from 'src/packages/checklistManagement/interfaces';
import { ChecklistService } from 'src/packages/checklistManagement/providers/checklist.service';
import {
  SelectableType,
  SelectedEvent,
} from 'src/packages/checklistManagement/types';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- tier-backlog
import { ExportOptionsHelper, ExportOptionsPopoverPage } from 'src/packages/templateManagement';
import { ExportHelper } from 'src/packages/transfer';
import {
  IResourceAssociation,
  RESOURCE_ASSOCIATIONS,
} from '../../interfaces/iResourceAssociation';
import { IChecklist } from '../../models/checklist';
import { instanceOfIChecklistDependency } from '../../models/checklist-dependency';
import { instanceOfIChecklistGroup } from '../../models/checklist-group';
import {
  IChecklistModule,
  instanceOfIChecklistModule,
} from '../../models/checklist-module';

export type ElementName = 'placeholder' | 'description' | 'value';

/** Editor-Sicht auf eine Übersetzung. */
interface EditorTranslation {
  key: string;
  locale: 'de' | 'en' | 'fr';
  value: string;
  id?: number;
  _destroy?: boolean;
}

/** Übersetzbares Element (Checkliste, Gruppe, Modul) für die Editor-Helfer. */
interface HasTranslations extends BasicModel {
  translations?: EditorTranslation[];
}
@Component({
  selector: 'app-checklist-details',
  templateUrl: './checklist-details.page.html',
  styleUrls: ['./checklist-details.page.scss'],
})
export class ChecklistDetailsPage
  extends DetailPageService<IChecklist>
  implements OnInit
{
  public service: IDataService<IChecklist> = this.checklistService;

  /**
   * Auswahl von möglichen Resourcen
   * @return mögliche Resourcen
   */
  public get resourceAssotiations(): IResourceAssociation[] {
    return RESOURCE_ASSOCIATIONS;
  }
  /**
   * Auswahl von möglichen Typen
   * @return mögliche Typen
   */
  public get typeAssotiations(): ITypeAssociation[] {
    return TYPE_ASSOCIATIONS;
  }
  /**
   * Auswahl von möglichen RegEx-Vorlagen
   * @return regex Vorlagen
   */
  public get regexAssotiations(): IRegExAssociation[] {
    return REGEX_ASSOCIATIONS;
  }

  selected!: SelectedEvent;

  /** Aktuell im Editor bearbeitete Sprache (de = Basisfelder, en/fr = Übersetzungen). */
  editLang: 'de' | 'en' | 'fr' = 'de';

  /** Vom System unterstützte Sprachen für die Sprach-Umschaltung. */
  get languages(): MitsLanguage[] {
    return this.languageService.supportedLanguages;
  }

  /**
   * Text eines Feldes in der aktuell gewählten Sprache: bei de das Basisfeld,
   * sonst die passende Übersetzung (leer, wenn keine vorhanden).
   */
  fieldText(item: HasTranslations, key: string): string {
    if (this.editLang === 'de') {
      const base = (item as unknown as Record<string, unknown>)[key];
      return typeof base === 'string' ? base : '';
    }
    return (
      item.translations?.find(t => t.locale === this.editLang && t.key === key)
        ?.value ?? ''
    );
  }

  /** Schreibt den Text eines Feldes in die aktuell gewählte Sprache. */
  setFieldText(item: HasTranslations, key: string, value: string): void {
    if (this.editLang === 'de') {
      (item as unknown as Record<string, unknown>)[key] = value;
      return;
    }
    if (!item.translations) {
      item.translations = [];
    }
    const existing = item.translations.find(
      t => t.locale === this.editLang && t.key === key
    );
    if (existing) {
      existing.value = value;
    } else {
      item.translations.push({ locale: this.editLang, key, value });
    }
  }

  /**
   * Bereinigt die Übersetzungen aller Ebenen vor dem Speichern: leere neue Einträge
   * werden verworfen, geleerte bestehende (mit id) als _destroy markiert.
   */
  private cleanAllTranslations(checklist: IChecklist): void {
    this.cleanList(checklist);
    for (const group of checklist.groups) {
      this.cleanList(group);
      for (const checklistModule of group.modules) {
        this.cleanList(checklistModule);
      }
    }
  }

  private cleanList(item: HasTranslations): void {
    if (!item.translations) {
      return;
    }
    item.translations = item.translations
      .map(t => (t.value ? t : t.id ? { ...t, _destroy: true } : null))
      .filter((t): t is EditorTranslation => t !== null);
  }

  constructor(
    public readonly accessGuard: AccessGuard,
    public readonly device: Platform,
    public readonly errorService: ErrorService,
    public readonly functionSwitchHelper: FunctionSwitchHelperService,
    public readonly location: Location,
    public readonly route: ActivatedRoute,
    public readonly router: Router,
    public readonly toastService: ToastService,
    public readonly toggleMenuHelper: ToggleMenuHelper,
    private readonly checklistService: ChecklistService,
    private readonly exportHelper: ExportHelper,
    private readonly exportOptionsHelper: ExportOptionsHelper,
    private readonly popoverCtrl: PopoverController,
    private readonly loadingController: LoadingController,
    private readonly navCtrl: NavController,
    private readonly languageService: LanguageService
  ) {
    super(
      accessGuard,
      device,
      errorService,
      functionSwitchHelper,
      location,
      route,
      router,
      toastService,
      toggleMenuHelper
    );
  }

  async ngOnInit() {
    const state = this.router.getCurrentNavigation()?.extras.state;
    await this.initializeDetail();
    // If Template is given in state, use it
    if (state?.checklist && !this._objectCreated) {
      this.object = state.checklist;
      console.debug(
        '[Checklists] Using checklist from given route',
        this.object
      );
    }
  }

  /**
   * Creating new blank Checklist Object
   * @return Neue leere checkliste
   */
  override newObject(): IChecklist {
    return {
      groups: [],
      name: 'Neue Checkliste',
      resource: 'Order',
      active: true,
      translations: [],
    } as unknown as IChecklist;
  }

  shouldShow(elementName: ElementName): boolean {
    const item = this.selected.item as IChecklistModule;
    switch (elementName) {
      case 'description':
        return item.type !== 'ChecklistManagement::Modules::SelectModule';

      case 'placeholder':
        return (
          item.type !== 'ChecklistManagement::Modules::SelectModule' &&
          item.type !== 'ChecklistManagement::Modules::GalleryModule' &&
          item.type !== 'ChecklistManagement::Modules::BooleanModule' &&
          item.type !== 'ChecklistManagement::Modules::DateModule'
        );

      default:
        return false;
    }
  }

  /**
   * Gets triggered if a new type gets selected
   * @param newType wich now is selected
   */
  changedElementType(newType: SelectableType): void {
    if (instanceOfIChecklistModule(this.selected.item))
      this.selected.item.description = '';
  }

  /**
   * Ist das Element einem bestimmten Typ entsprechend?
   * @param type to check for
   * @returns if from type
   */
  isSelectedType(type: string): boolean {
    let isType = false;
    switch (type) {
      case 'IChecklistGroup':
        isType = instanceOfIChecklistGroup(this.selected.item);
        break;

      case 'IChecklistModule':
        isType = instanceOfIChecklistModule(this.selected.item);
        break;

      case 'IChecklistDependency':
        isType = instanceOfIChecklistDependency(this.selected.item);
        break;
    }
    return isType;
  }

  /**
   * Exports the template
   */
  async exportChecklist() {
    const removeAdditional = [];
    if (this.exportOptionsHelper.exportOptions.removeRemoteIds)
      removeAdditional.push('remote_id');
    this.exportHelper.exportJson(
      this.object,
      'ChecklistManagement::Checklist',
      true,
      removeAdditional
    );
  }

  /**
   * Opens the menu to edit the export options
   */
  async openExportOptions(ev: Event) {
    const popover = await this.popoverCtrl.create({
      component: ExportOptionsPopoverPage,
      event: ev,
      cssClass: 'export-options-popover',
    });
    popover.present();
  }

  /**
   * Checkliste speichern
   */
  async saveChecklist() {
    const loading = await this.loadingController.create({
      spinner: 'crescent',
      message: $localize`Checkliste wird gespeichert...`,
      translucent: true,
    });
    loading.present();
    this.cleanAllTranslations(this.object);
    this.checklistService.saveRemote(this.object).subscribe(
      (res) => {
        this.navCtrl.navigateRoot(['checklists/'], {
          queryParams: { reloadMitsTable: true },
        });
        loading.dismiss();
      },
      (err) => {
        loading.dismiss();
      }
    );
  }

  /**
   * Gib den Titel der Seite zurück
   */
  get pageTitle(): string {
    return this._objectCreated ? $localize`Checkliste bearbeiten` : $localize`Checkliste erstellen`
  }
}
